import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Archive, Inbox, Loader2, Mail, PenSquare, RefreshCw, Search, Send, Settings2, Trash2, Zap } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import MessageList from "@/components/mailbox/MessageList";
import MessageView from "@/components/mailbox/MessageView";
import Composer from "@/components/mailbox/Composer";
import TemplateManager from "@/components/mailbox/TemplateManager";

const FOLDERS = [
  { key: "inbox", icon: Inbox, label: "mb_inbox" },
  { key: "sent", icon: Send, label: "mb_sent" },
  { key: "archive", icon: Archive, label: "mb_archive" },
  { key: "trash", icon: Trash2, label: "mb_trash" },
];

export default function Mailbox() {
  const { t } = useLang();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [access, setAccess] = useState(null);
  const [folder, setFolder] = useState(["inbox", "sent", "archive", "trash"].includes(params.get("folder")) ? params.get("folder") : "inbox");
  const [q, setQ] = useState(params.get("q") || "");
  const [page, setPage] = useState(1);
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(params.get("open") || null);
  const [compose, setCompose] = useState(null);
  const [tplOpen, setTplOpen] = useState(false);
  const isAdmin = ["admin", "superadmin"].includes(user?.role);

  useEffect(() => { api.get("/mailbox/access").then(({ data }) => setAccess(data)).catch(() => setAccess({ allowed: false })); }, []);

  const load = useCallback(async () => {
    if (!access?.configured || !access?.allowed) return;
    setLoading(true);
    try { const { data } = await api.get("/mailbox/messages", { params: { folder, page, q } }); setList(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [access, folder, page, q]);

  useEffect(() => { load(); }, [load]);

  const pick = (f) => { setFolder(f); setPage(1); setSelected(null); };
  const search = (e) => { e.preventDefault(); setPage(1); setParams(q ? { q } : {}); load(); };

  if (!access) return <div className="p-8 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />{t("loading")}</div>;
  if (!access.allowed) return <div className="p-8 text-sm text-muted-foreground" data-testid="mailbox-no-access">{t("mb_no_access")}</div>;
  if (!access.configured) {
    return (
      <div className="p-8 space-y-3" data-testid="mailbox-not-configured">
        <p className="font-display font-bold text-lg flex items-center gap-2"><Mail className="h-5 w-5" />{t("nav_mailbox")}</p>
        <p className="text-sm text-muted-foreground">{t("mb_not_configured")}</p>
        {isAdmin && <Button asChild variant="outline"><Link to="/admin/mailserver" data-testid="mailbox-goto-settings"><Settings2 className="h-4 w-4 mr-1" />{t("admin_email")}</Link></Button>}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]" data-testid="mailbox-page">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-border bg-card">
        <p className="font-display font-bold text-lg flex items-center gap-2"><Mail className="h-5 w-5" />{t("nav_mailbox")}
          <span className="text-xs font-normal text-muted-foreground font-mono" data-testid="mailbox-address">{access.address}</span></p>
        <form onSubmit={search} className="flex items-center gap-2 ml-auto">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("mb_search")} className="pl-8 w-64" data-testid="mailbox-search" />
          </div>
          <Button type="submit" variant="outline" size="sm" data-testid="mailbox-search-button">{t("mb_do_search")}</Button>
          <Button type="button" variant="outline" size="sm" onClick={load} disabled={loading} data-testid="mailbox-refresh"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></Button>
          {isAdmin && <Button type="button" variant="outline" size="sm" onClick={() => setTplOpen(true)} title={t("mb_templates")} data-testid="mailbox-templates"><Zap className="h-4 w-4" /></Button>}
          <Button type="button" size="sm" onClick={() => setCompose({ mode: "new" })} data-testid="mailbox-compose"><PenSquare className="h-4 w-4 mr-1" />{t("mb_new")}</Button>
        </form>
      </div>

      <div className="flex flex-1 min-h-0">
        <aside className="w-44 shrink-0 border-r border-border bg-[hsl(var(--sidebar))] p-2 space-y-0.5" data-testid="mailbox-folders">
          {FOLDERS.map((f) => {
            const Icon = f.icon; const active = folder === f.key;
            return (
              <button key={f.key} type="button" onClick={() => pick(f.key)} data-testid={`mailbox-folder-${f.key}`}
                className={`w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
                <Icon className="h-4 w-4" />{t(f.label)}
                {f.key === "inbox" && list?.unseen > 0 && folder === "inbox" && <span className="ml-auto text-[10px] font-bold rounded-full bg-rose-500 text-white px-1.5" data-testid="mailbox-unseen">{list.unseen}</span>}
              </button>
            );
          })}
        </aside>

        <MessageList list={list} loading={loading} selected={selected} onSelect={setSelected} page={page} setPage={setPage} folder={folder} />

        <section className="flex-1 min-w-0 bg-background overflow-y-auto cb-scroll">
          {selected
            ? <MessageView uid={selected} folder={folder} onChanged={() => { setSelected(null); load(); }}
                onReply={(m, mode, extra) => setCompose({ mode, orig: m, ...(extra || {}) })} />
            : <div className="h-full flex items-center justify-center text-sm text-muted-foreground" data-testid="mailbox-empty-view">{t("mb_pick")}</div>}
        </section>
      </div>

      {tplOpen && <TemplateManager onClose={() => setTplOpen(false)} />}
      {compose && <Composer {...compose} onClose={(sent) => { setCompose(null); if (sent) load(); }} />}
    </div>
  );
}
